import { Button, Skeleton, Stack, Typography as T } from "@mui/material";
import CreateDataset from "datasets/components/CreateDataset";
import EmptyStateText from "datasets/components/EmptyStateText";
import useDatasets from "datasets/hooks/useDatasets";
import { CardsIcon, DatasetCardsIcon, PlusIcon } from "~/assets/icons";
import CardGrid from "~/components/CardGrid";
import DatasetCard from "~/components/DatasetCard";
import useUserPermission from "~/hooks/useUserPermission";

const Skeletons = ({ size }: { size: "small" | "medium" }) => (
  <>
    {Array.from({ length: size === "medium" ? 9 : 4 }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rounded"
        sx={{ borderRadius: "8px", height: size === "medium" ? 210 : 220, display: "flex" }}
      />
    ))}
  </>
);

const EmptyDatasets = ({ size = "medium" }: { size?: "small" | "medium" }) => {
  const hasCreatePermission = useUserPermission("create:catalog-dataset");
  const EMPTY_MESSAGE = "You don't have access to any datasets in your organization.";

  return (
    <Stack
      alignItems="center"
      data-testid="dataset-list-empty"
      gap={size === "small" ? 3 : undefined}
      mt={size === "medium" ? 13 : undefined}
    >
      {size === "small" && (
        <>
          <CardsIcon />
          <T variant="body1" color="text.secondary">
            {EMPTY_MESSAGE}
          </T>
        </>
      )}
      {size === "medium" && (
        <>
          <EmptyStateText>No datasets found</EmptyStateText>
          <T variant="body1" color="text.secondary">
            {EMPTY_MESSAGE}
          </T>

          <DatasetCardsIcon style={{ maxWidth: 520, marginBlock: "28px 36px" }} />
        </>
      )}
      {hasCreatePermission && (
        <CreateDataset
          callToAction={
            <Button
              color="primary"
              variant="contained"
              aria-label="Create dataset"
              startIcon={<PlusIcon />}
            >
              New Dataset
            </Button>
          }
        />
      )}
    </Stack>
  );
};

const DatasetList = ({
  limit,
  showEmpty,
  size = "medium",
}: {
  limit?: number;
  showEmpty: boolean;
  size?: "small" | "medium";
}) => {
  const { data: datasets, isLoading, error } = useDatasets();
  const partialDatasets = datasets?.slice(0, limit);

  if (showEmpty) {
    return <EmptyDatasets size={size} />;
  }

  return (
    <CardGrid
      data-testid="dataset-list"
      minWidth={size === "medium" ? 350 : undefined}
      sx={{ gap: size === "medium" ? 3 : 2 }}
    >
      {isLoading && !error ? (
        <Skeletons size={size} />
      ) : (
        partialDatasets?.map((dataset) => <DatasetCard key={dataset.id} size={size} {...dataset} />)
      )}
    </CardGrid>
  );
};

export default DatasetList;
